import React from "react";
import { DropdownOption, DropdownWithSearchProps } from ".";
import clsx from "clsx";
import { IconChevronRight } from "@tabler/icons-react";
import { Combobox } from "@headlessui/react";
import Tooltip from "components/Tooltip";
import { useTranslation } from "react-i18next";

interface OptionItem<T> extends Omit<DropdownWithSearchProps<T>, "items"> {
  item: T;
  index: number;
  handleOnClick: (item: T) => void;
  details?: T | null;
  query?: string | number;
  selected?: boolean;
  hasChildren?: boolean;
  focusedIndex?: number;
}

function OptionItem<Item extends object>(props: OptionItem<Item>) {
  const { item, index, handleOnClick, details, query, selected } = props;
  const { t } = useTranslation();

  const dropdownItem = item as DropdownOption;

  function showShowPressEnter() {
    return (
      index === 0 &&
      props.focusedIndex === 0 &&
      props.searchTermAsOption &&
      dropdownItem.id === "search_term"
    );
  }

  function itemContent(item: Item) {
    return (
      <label className="truncate cursor-pointer">
        <div className="flex items-center">
          {dropdownItem.icon && (
            <div className="mr-2 min-w-[16px] flex justify-center">
              {dropdownItem.icon}
            </div>
          )}
          <div className="w-full">
            <div className="flex w-full items-center">
              {props.displayKey in item ? (
                dropdownItem.id === "search_term" ? (
                  <div className="flex justify-between">
                    <div>{`“${query}” ${t("commons.use")}`}</div>
                  </div>
                ) : (
                  <Tooltip truncate className="max-w-[400px]">
                    {(item[props.displayKey] as string) || " "}
                  </Tooltip>
                )
              ) : null}
              {showShowPressEnter() && (
                <div className="text-indigo-500 text-sm font-normal ml-3">
                  {t("commons.pressEnter")}
                </div>
              )}
            </div>
            <span
              className={clsx("text-gray-500 group-hover:text-indigo-500", {
                "text-indigo-500": selected,
              })}
            >
              {(item[props.detailKey as keyof Item] as string) ?? ""}
            </span>
          </div>
        </div>

        {details ? (
          <div className="text-gray-500 whitespace-normal pl-[19px] group-hover:text-indigo-500">
            {details && props.displayKey === "name"
              ? (details as DropdownOption)?.name
              : (details as DropdownOption)?.name_de ??
                (details as DropdownOption)?.name}
          </div>
        ) : null}
      </label>
    );
  }

  const optionWrapperStyle = `relative w-full ${
    props.disabled ? "cursor-not-allowed" : "cursor-pointer"
  }`;

  return (
    <div
      className={clsx(
        "flex cursor-pointer group items-center text-gray-700 hover:bg-indigo-50 px-4 py-2 hover:text-indigo-700",
        {
          "bg-indigo-50 text-indigo-700":
            selected ||
            (props.focusedIndex === props.index && props.searchTermAsOption),
        },
        props.optionRowClassName
      )}
      onClick={() => handleOnClick(item)}
    >
      <div className="flex overflow-hidden text-sm font-medium truncate w-full">
        {props.hasChildren ? (
          <div key={index} className={optionWrapperStyle}>
            {itemContent(item)}
          </div>
        ) : (
          <Combobox.Option
            key={index}
            value={item}
            className={optionWrapperStyle}
          >
            {itemContent(item)}
          </Combobox.Option>
        )}
      </div>
      {props.hasChildren && !query ? <IconChevronRight size={20} /> : null}
    </div>
  );
}

export default OptionItem;
